import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { get } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad, useQueryState, useOnChange, useRememberListUrl } from "../lib/hooks";
import { cadenceText } from "../lib/format";
import { Avatar, Button, Card, DueBadge, Empty, ErrorBox, Loading, PageHead, Pager, SearchInput, Select, Tabs, TierBadge } from "../ui";
import { LastTouchCell, RowActions, SaveViewButton, useSavedViews } from "../components/ListParts";
import { CONTACT_SORTS, contactListParams, defaultSort, dueViewCounts } from "../lib/lists";
import { useShell } from "../shell/context";

const PAGE_SIZE = 50;

const BUILTIN = [
    { key: "all", label: "All" },
    { key: "overdue", label: "Overdue" },
    { key: "due_soon", label: "Due soon" },
    { key: "snoozed", label: "Snoozed" },
    { key: "role_changed", label: "Role changed" },
];

const roleLine = (c) => (c.current_role ? [c.current_role.title, c.current_role.company_name].filter(Boolean).join(" at ") : "");

export default function People() {
    useDocumentTitle("People");
    useRememberListUrl();
    const history = useHistory();
    const { version, bump, openCreate } = useShell();
    const saved = useSavedViews("contacts");
    const [view, setView] = useQueryState("view", "all");
    const [q, setQ] = useQueryState("q", "");
    const [tier, setTier] = useQueryState("tier", "");
    const [tag, setTag] = useQueryState("tag", "");
    const [sort, setSort] = useQueryState("sort", "");
    const [page, setPage] = useQueryState("page", 1);
    const dq = useDebounced(q, 250);

    const custom = saved.views.find((v) => v.id === view);
    const base = custom ? custom.view : view;

    useOnChange([dq, tier, tag, sort, view], () => setPage(1));

    const tags = useLoad(() => get("v1/crm/tags"), []);
    const counts = useLoad(() => dueViewCounts("v1/crm/contacts"), [version]);

    // Every view, filter and sort runs on the server (API polish), paged.
    const list = useLoad(
        () => get("v1/crm/contacts", contactListParams({ view: base, q: dq, tier, tag, sort, page, pageSize: PAGE_SIZE })),
        [base, dq, tier, tag, sort, page, version]
    );
    const rows = (list.data && list.data.data) || [];

    const pickView = (key) => {
        setView(key);
        const v = saved.views.find((x) => x.id === key);
        if (v) {
            setQ(v.q || "");
            setTier(v.tier || "");
            setTag(v.tag || "");
            setSort(v.sort || "");
        } else {
            setSort("");
        }
    };

    const c = counts.data || {};
    const tabs = [
        ...BUILTIN.map((b) => ({
            key: b.key,
            label: b.label,
            count: b.key === "overdue" ? c.overdue : b.key === "due_soon" ? c.due_soon : b.key === "snoozed" ? c.snoozed : b.key === view && list.data ? list.data.count : undefined,
            danger: b.key === "overdue",
        })),
        ...saved.views.map((v) => ({ key: v.id, label: v.name, onRemove: () => { saved.remove(v.id); if (view === v.id) setView("all"); } })),
    ];

    return (
        <>
            <PageHead
                crumb="CRM"
                title="People"
                sub={list.data ? `${list.data.count} ${list.data.count === 1 ? "person" : "people"} in this view` : " "}
                actions={
                    <>
                        <Button variant="soft-linkedin" icon="ri-linkedin-box-fill" onClick={() => history.push("/crm/imports")}>Import LinkedIn</Button>
                        <Button variant="primary" icon="ri-add-line" onClick={() => openCreate("contact")}>New person</Button>
                    </>
                }
            />
            <Card noBody>
                <Tabs tabs={tabs} active={view} onChange={pickView} />
                <div className="hz-filterbar">
                    <SearchInput value={q} onChange={setQ} placeholder="Filter by name, email or LinkedIn URL…" />
                    <Select value={tier} onChange={(e) => setTier(e.target.value)} placeholder="Any tier" options={[{ value: "A", label: "Tier A" }, { value: "B", label: "Tier B" }, { value: "C", label: "Tier C" }, { value: "none", label: "No tier" }]} />
                    <Select
                        value={tag}
                        onChange={(e) => setTag(e.target.value)}
                        placeholder="Any tag"
                        options={((tags.data && tags.data.data) || []).map((t) => ({ value: t.name, label: `${t.name} (${t.usage_count})` }))}
                    />
                    <Select
                        value={sort || defaultSort(base)}
                        onChange={(e) => setSort(e.target.value)}
                        title="Sort"
                        options={CONTACT_SORTS.map((o) => ({ value: o.value, label: `Sort: ${o.label}` }))}
                    />
                    <SaveViewButton onSave={(name) => saved.add({ name, view: base, q, tier, tag, sort })} />
                </div>
                <ErrorBox error={list.error} onRetry={list.reload} />
                {list.loading && !list.data ? (
                    <Loading />
                ) : (
                    <div className="hz-table-wrap">
                        <table className="hz-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Tier</th>
                                    <th>Due</th>
                                    <th>Last touch</th>
                                    <th>Cadence</th>
                                    <th>Tags</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className={r.due_status === "overdue" ? "overdue" : ""}>
                                        <td>
                                            <Link to={`/crm/contacts/${r.id}`} className="hz-entity-cell">
                                                <Avatar name={r.name} />
                                                <span style={{ minWidth: 0 }}>
                                                    <span className="hz-entity-name">{r.name}</span>
                                                    <span className="hz-entity-sub">{roleLine(r) || r.email || ""}</span>
                                                </span>
                                            </Link>
                                        </td>
                                        <td><TierBadge tier={r.tier} custom={!!r.cadence_days_override} showNone /></td>
                                        <td><DueBadge status={r.due_status} dueInDays={r.due_in_days} snoozedUntil={r.snoozed_until} withText /></td>
                                        <td><LastTouchCell date={r.last_touch_at} /></td>
                                        <td className="hz-nowrap hz-small" style={{ color: "var(--hz-gray-700)" }}>{cadenceText(r)}</td>
                                        <td>
                                            <div className="hz-row-wrap" style={{ gap: 4 }}>
                                                {(r.tags || []).map((t) => <span key={t} className="hz-tag">{t}</span>)}
                                            </div>
                                        </td>
                                        <td className="actions"><RowActions kind="contact" row={r} onChanged={bump} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rows.length === 0 && !list.error && <Empty>No people match this view.</Empty>}
                    </div>
                )}
                {list.data && <Pager page={page} maxPage={list.data.max_page} count={list.data.count} onPage={setPage} label="people" />}
            </Card>
        </>
    );
}
